import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import type { Prayer } from '../../types';

const CHURCH_SLUG = 'brisas';
const BG = '#1C1C1E';
const CARD = '#2C2C2E';
const CARD2 = '#3A3A3C';

export default function PrayerDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [prayer, setPrayer] = useState<Prayer | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [churchId, setChurchId] = useState<string | null>(null);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [isPublic, setIsPublic] = useState(true);

  useEffect(() => {
    supabase.from('churches').select('id').eq('slug', CHURCH_SLUG).single()
      .then(({ data }) => { if (data) setChurchId(data.id); });
  }, []);

  useEffect(() => {
    if (id === 'new') { setLoading(false); return; }
    supabase.from('prayers').select('*').eq('id', id!).single()
      .then(({ data }) => { setPrayer(data); setLoading(false); });
  }, [id]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim() || !description.trim() || !churchId) return;
    setSubmitting(true);
    const { error } = await supabase.from('prayers').insert({
      church_id: churchId, user_id: null,
      title: title.trim(), description: description.trim(),
      is_public: isPublic, is_answered: false,
    });
    setSubmitting(false);
    if (!error) { setSuccess(true); setTimeout(() => navigate('/prayers'), 2000); }
  };

  const formatDate = (d: string) =>
    new Date(d).toLocaleDateString('es-MX', { day: 'numeric', month: 'long', year: 'numeric' });

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: BG }}>
      <div className="animate-spin rounded-full h-8 w-8 border-4 border-t-transparent" style={{ borderColor: '#4A90D9', borderTopColor: 'transparent' }} />
    </div>
  );

  // Formulario nueva petición
  if (id === 'new') {
    if (success) return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: BG }}>
        <div className="rounded-lg p-8 text-center" style={{ backgroundColor: CARD }}>
          <div className="text-5xl mb-4">✅</div>
          <h2 className="text-xl font-bold text-white mb-2">¡Petición enviada!</h2>
          <p style={{ color: '#8E8E93' }}>La comunidad orará por ti.</p>
        </div>
      </div>
    );

    return (
      <div className="min-h-screen" style={{ backgroundColor: BG }}>
        <div className="px-4 pt-4 pb-5" style={{ background: 'linear-gradient(180deg, #0D2B8E 0%, #1C1C1E 100%)' }}>
          <Link to="/prayers" className="text-sm mb-3 inline-block" style={{ color: '#4A90D9' }}>← Volver</Link>
          <h1 className="text-2xl font-bold text-white">Nueva Petición</h1>
          <p className="text-sm mt-1" style={{ color: '#8E8E93' }}>Comparte tu petición con la comunidad</p>
        </div>

        <div className="p-4">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1.5" style={{ color: '#8E8E93' }}>
                Título <span style={{ color: '#FF453A' }}>*</span>
              </label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="w-full px-4 py-3 rounded-lg text-white outline-none"
                style={{ backgroundColor: CARD, border: '1px solid #3A3A3C' }}
                placeholder="Ej: Oración por mi familia"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1.5" style={{ color: '#8E8E93' }}>
                Petición <span style={{ color: '#FF453A' }}>*</span>
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="w-full px-4 py-3 rounded-lg text-white outline-none resize-none h-32"
                style={{ backgroundColor: CARD, border: '1px solid #3A3A3C' }}
                placeholder="Escribe los detalles..."
                required
              />
            </div>

            <div className="flex items-center justify-between py-1">
              <span className="text-sm font-medium text-white">Compartir con la comunidad</span>
              <button
                type="button"
                onClick={() => setIsPublic(!isPublic)}
                className="w-12 h-6 rounded-full transition-colors"
                style={{ backgroundColor: isPublic ? '#4A90D9' : CARD2 }}
              >
                <div className={`w-5 h-5 bg-white rounded-full shadow transform transition-transform ${isPublic ? 'translate-x-6' : 'translate-x-0.5'}`} />
              </button>
            </div>

            <button
              type="submit"
              disabled={submitting || !title.trim() || !description.trim()}
              className="w-full py-3 rounded-lg font-bold text-white transition-opacity disabled:opacity-40"
              style={{ backgroundColor: '#4A90D9' }}
            >
              {submitting ? 'Enviando...' : 'Enviar petición'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  if (!prayer) return (
    <div className="text-center py-16" style={{ backgroundColor: BG }}>
      <p style={{ color: '#8E8E93' }}>Petición no encontrada</p>
      <Link to="/prayers" className="mt-3 block" style={{ color: '#4A90D9' }}>← Volver</Link>
    </div>
  );

  return (
    <div className="min-h-screen" style={{ backgroundColor: BG }}>
      <div className="px-4 pt-4 pb-5" style={{ background: 'linear-gradient(180deg, #0D2B8E 0%, #1C1C1E 100%)' }}>
        <Link to="/prayers" className="text-sm mb-3 inline-block" style={{ color: '#4A90D9' }}>← Volver</Link>
        <div className="flex items-center gap-2">
          <span className="text-3xl">{prayer.is_answered ? '✅' : '🙏'}</span>
          {prayer.is_answered && (
            <span className="text-xs px-2 py-1 rounded" style={{ backgroundColor: '#3A3A3C', color: '#30D158' }}>Respondida</span>
          )}
        </div>
        <h1 className="text-xl font-bold text-white mt-2">{prayer.title}</h1>
        <p className="text-sm mt-1" style={{ color: '#8E8E93' }}>📅 {formatDate(prayer.created_at)}</p>
      </div>

      <div className="p-4 space-y-3">
        <div className="rounded-lg p-4" style={{ backgroundColor: CARD }}>
          <p className="leading-relaxed whitespace-pre-wrap" style={{ color: '#E5E5EA' }}>{prayer.description}</p>
        </div>

        {prayer.is_answered && prayer.answer_note && (
          <div className="rounded-lg p-4" style={{ backgroundColor: CARD }}>
            <p className="font-semibold mb-2" style={{ color: '#30D158' }}>✝ Respuesta</p>
            <p style={{ color: '#E5E5EA' }}>{prayer.answer_note}</p>
          </div>
        )}
      </div>
    </div>
  );
}
